import { useEffect, useMemo, useState, useCallback } from "react";
import { api, inr, PAYMENT_MODES } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const emptyPayment = { project_id: "", amount: 0, direction: "in", mode: "bank_transfer", payment_date: "", reference: "", notes: "" };

export default function FinancePage() {
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({ total_received: 0, total_paid_out: 0, total_pipeline: 0, pending_balance: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPayment);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/payments").then((r) => setPayments(r.data));
    api.get("/payments/summary").then((r) => setSummary(r.data));
  }, []);
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, [load]);

  const submit = async () => {
    if (!form.amount) { toast.error("Amount required"); return; }
    try {
      if (editId) await api.patch(`/payments/${editId}`, form);
      else await api.post("/payments", form);
      setOpen(false); load(); toast.success("Saved");
      setForm(emptyPayment); setEditId(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.client_name || "—";

  return (
    <div className="space-y-4" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Money</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Payments</h1>
        </div>
        <Button onClick={() => { setEditId(null); setForm(emptyPayment); setOpen(true); }} data-testid="new-payment-btn">
          <Plus className="h-4 w-4 mr-2" />Add Payment
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SummaryTile label="Total Pipeline" value={inr(summary.total_pipeline)} tone="brand" />
        <SummaryTile label="Total Received" value={inr(summary.total_received)} icon={ArrowDownRight} tone="success" />
        <SummaryTile label="Total Paid Out" value={inr(summary.total_paid_out)} icon={ArrowUpRight} tone="warning" />
        <SummaryTile label="Pending Balance" value={inr(summary.pending_balance)} tone="critical" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <div className="p-4 border-b border-border">
          <h3 className="font-display font-semibold">Payment Records</h3>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Project</TableHead>
            <TableHead>Direction</TableHead><TableHead>Mode</TableHead>
            <TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} className="cursor-pointer" onClick={() => { setEditId(p.id); setForm({ ...p }); setOpen(true); }} data-testid={`payment-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.payment_date || "—"}</TableCell>
                <TableCell>{projectName(p.project_id)}</TableCell>
                <TableCell>
                  <Badge className={p.direction === "in" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border" : "bg-amber-500/20 text-amber-300 border-amber-500/30 border"}>
                    {p.direction === "in" ? "Received" : "Paid Out"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm capitalize">{p.mode?.replace(/_/g, " ")}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.reference || "—"}</TableCell>
                <TableCell className={`text-right font-mono font-semibold ${p.direction === "in" ? "text-emerald-400" : "text-amber-400"}`}>
                  {p.direction === "in" ? "+" : "−"} {inr(p.amount)}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No payments logged yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>{editId ? "Edit Payment" : "Add Payment"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <PField label="Project" hint="Which project is this payment for?">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="payment-project-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </PField>
            <div className="grid grid-cols-2 gap-3">
              <PField label="Amount (₹)" required>
                <Input type="number" placeholder="0" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="payment-amount-input" />
              </PField>
              <PField label="Payment Date">
                <Input type="date" value={form.payment_date || ""} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
              </PField>
              <PField label="Direction" hint="Money in (client → us) or out (us → vendor)">
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Received (client → us)</SelectItem>
                    <SelectItem value="out">Paid out (us → vendor)</SelectItem>
                  </SelectContent>
                </Select>
              </PField>
              <PField label="Payment Mode">
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </PField>
            </div>
            <PField label="Reference" hint="UTR / cheque no. / txn id">
              <Input placeholder="e.g. UTR12345678" value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </PField>
            <PField label="Notes" hint="Optional context">
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </PField>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="payment-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const tones = {
    brand: "text-[hsl(var(--brand-yellow))]",
    success: "text-emerald-400",
    warning: "text-amber-400",
    critical: "text-red-400",
  };
  return (
    <Card className="p-4 bg-card border-border">
      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
    </Card>
  );
}
